import { AnimatePresence, motion } from 'framer-motion';
import { PuzzleIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { BsLayersFill } from 'react-icons/bs';
import { HiArrowLeft, HiArrowRight, HiCheck, HiOutlineViewBoards } from 'react-icons/hi';
import { IoLayers } from 'react-icons/io5';
import CustomButton from '../../../components/common/CustomButton';

// ... (existing code)


interface AboutWorkItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const steps = [
  {
    id: "strategy_map",
    title: "Strategy Map",
    description:
      "Group epics and features from tools like Jira and Asana into an AI-assisted strategy view to manage scope, timelines, budgets, and portfolio risk",
    icon: HiOutlineViewBoards,
    color: "text-sky-600 dark:text-sky-400",
    flow: "Epic / Feature / Task",
  },
  // {
  //   id: "strategy_map",
  //   title: "Why Strategy Map?",
  //   description:
  //     "Strategy maps is a way to enable our powerful AI features, see this as a Executive entity to manage Portfolio Risk in terms of Scope, timeline and Budgets. e.g. Creating portfolio based budget vs cost simulations. AI to identify risk etc.",
  //   color: "text-sky-600 dark:text-sky-400",
  // },
];

const AboutSmapModel: React.FC<AboutWorkItemsModalProps> = ({ isOpen, onClose }) => {

  const [currentStep, setCurrentStep] = useState(0);

  // Reset step when opened
  useEffect(() => {
    if (isOpen) setCurrentStep(0);
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col min-h-[460px]"
      >
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center w-full"
            >
              {/* Icon / Viz */}
              {/* Icon / Viz */}
              <div className="mb-8 relative h-32 flex items-center justify-center">
                {steps[currentStep].id === "strategy_map" ? (
                  /* Strategy Map Animation */
                  <div className="relative w-32 h-32 flex items-center justify-center">
                    {/* Background Blur */}
                    <div className="absolute inset-0 bg-sky-500/10 rounded-full blur-[40px]" />

                    {/* Center: Strategy Map */}
                    <motion.div
                      className="relative z-10 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <HiOutlineViewBoards className="text-5xl text-sky-600 dark:text-sky-400" />
                    </motion.div>

                    {/* Satellite 1: Feature (Top) */}
                    <motion.div
                      className="absolute -top-2 left-1/2 -translate-x-1/2 z-20 bg-white dark:bg-slate-800 p-2 rounded-lg shadow-sm border border-gray-100 dark:border-slate-700"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3, type: "spring", damping: 10 }}
                    >
                      <PuzzleIcon className="text-xl text-sky-400" />
                    </motion.div>

                    {/* Satellite 2: Normal Epic (Bottom Left) */}
                    <motion.div
                      className="absolute -bottom-1 -left-1 z-20 bg-white dark:bg-slate-800 p-2 rounded-lg shadow-sm border border-gray-100 dark:border-slate-700"
                      initial={{ x: 20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.5, type: "spring", damping: 10 }}
                    >
                      <BsLayersFill className="text-xl text-violet-400" />
                    </motion.div>

                    {/* Satellite 3: Advanced Epic (Bottom Right) */}
                    <motion.div
                      className="absolute -bottom-1 -right-1 z-20 bg-white dark:bg-slate-800 p-2 rounded-lg shadow-sm border border-gray-100 dark:border-slate-700"
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.7, type: "spring", damping: 10 }}
                    >
                      <IoLayers className="text-xl text-violet-400" />
                    </motion.div>
                  </div>
                ) : steps[currentStep].id === "intro" ? (
                  /* Intro Spinner Logo */
                  <div className="relative">
                    <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-blue-500/10 dark:bg-blue-500/10 rounded-full blur-[40px] pointer-events-none" />
                    <motion.img
                      src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
                      className="h-24 w-24 object-contain"
                      alt="Loading..."
                      animate={{ rotate: 360 }}
                      transition={{
                        repeat: Infinity,
                        duration: 10,
                        ease: "linear",
                      }}
                    />
                  </div>
                ) : (
                  /* Step Icon */
                  <div className="relative">
                    <div
                      className={`absolute top-[-50%] left-[-50%] w-[200%] h-[200%] rounded-full blur-[40px] pointer-events-none ${
                        steps[currentStep].color.includes("sky")
                          ? "bg-sky-500/10"
                          : "bg-violet-500/10"
                      }`}
                    />
                    <div className="p-6 bg-gray-50 dark:bg-slate-800/50 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm relative z-10">
                      {React.createElement(steps[currentStep].icon as any, {
                        className: `text-5xl ${steps[currentStep].color}`,
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {steps[currentStep].title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6 max-w-sm text-sm">
                {steps[currentStep].description}
              </p>

              {/* Flow Pill (if applicable) */}
              {steps[currentStep].flow && (
                <div className="px-4 py-1.5 bg-gray-100 dark:bg-slate-800 rounded-full text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-slate-700 mb-2">
                  {steps[currentStep].flow}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="relative p-6 border-t border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/50 ">
          {currentStep > 0 && (
            <div className="flex">
              <CustomButton
                variant="secondary"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <HiArrowLeft className="text-lg" />
              </CustomButton>
            </div>
          )}
          <div className="flex gap-1.5 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentStep
                    ? "w-6 bg-blue-600 dark:bg-blue-500"
                    : "w-1.5 bg-gray-200 dark:bg-slate-700"
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2 w-full">
            <CustomButton
              onClick={handleNext}
              className="flex items-center gap-2 ml-auto"
            >
              {currentStep === steps.length - 1 ? (
                <>
                  Get Started <HiCheck className="text-lg" />
                </>
              ) : (
                <>
                  Next <HiArrowRight className="text-lg" />
                </>
              )}
            </CustomButton>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AboutSmapModel;
